import { useTheme } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { useTenantTranslation } from "hooks/formatters";
import { useMemo } from "react";
import {
  ChemicalElementSymbol,
  ChemicalSampleLocation,
  ChemicalSamplePositionType,
  FailureMetadata,
  NonElementQuantity,
  ProductGroupName,
  TimeAggregation,
} from "src/store/api/generatedApi";
import { useFormatNumber } from "src/utils/formatNumber";
import { ColouredCell, ColourGradientCell } from "../ColouredCell";
import { chemicalElementColours } from "../plotting";
import { defaultTableSx } from "../tableFormatting";
import { GroupedFailureRateSeries } from "../types";

interface FailureRateTableProps {
  groupedFailureRateSeries: GroupedFailureRateSeries;
  selectedDate: Date | null;
  selectedTimeAggregation: TimeAggregation | null;
  selectedSampleLocation: ChemicalSampleLocation | null;
  selectedSamplePositionType: ChemicalSamplePositionType | null;
  selectedProductGroup: ProductGroupName | null;
}

type FailureRow = FailureMetadata & {
  id: number;
  chemicalElementSymbol: ChemicalElementSymbol | NonElementQuantity;
};

export const FailureRateTable = ({
  groupedFailureRateSeries,
  selectedDate,
  selectedTimeAggregation,
  selectedSampleLocation,
  selectedSamplePositionType,
  selectedProductGroup,
}: FailureRateTableProps) => {
  const rows = useMemo(() => {
    return buildRows(
      groupedFailureRateSeries,
      selectedDate,
      selectedTimeAggregation,
      selectedSampleLocation,
      selectedSamplePositionType,
      selectedProductGroup
    );
  }, [
    groupedFailureRateSeries,
    selectedDate,
    selectedTimeAggregation,
    selectedSampleLocation,
    selectedSamplePositionType,
    selectedProductGroup,
  ]);

  const columns = useBuildColumns();

  return (
    <HotkeyCompliantDataGrid
      sx={{ ...defaultTableSx, height: 550 }}
      rows={rows}
      columns={columns}
    />
  );
};

const buildRows = (
  groupedFailureRateSeries: GroupedFailureRateSeries,
  selectedDate: Date | null,
  selectedTimeAggregation: TimeAggregation | null,
  selectedSampleLocation: ChemicalSampleLocation | null,
  selectedSamplePositionType: ChemicalSamplePositionType | null,
  selectedProductGroup: ProductGroupName | null
): FailureRow[] => {
  const filteredSeries = groupedFailureRateSeries.values.filter(
    (item) =>
      item[0].timeAggregation === selectedTimeAggregation &&
      item[0].sampleLocation === selectedSampleLocation &&
      item[0].samplePositionType === selectedSamplePositionType &&
      item[0].productGroup === selectedProductGroup
  );

  const relevantFailures: FailureRow[] = [];
  let rowId = 0;

  for (const [key, series] of filteredSeries) {
    for (const [index, dateString] of series.date.entries()) {
      const date = new Date(dateString);

      if (!selectedDate || date.toISOString() === selectedDate.toISOString()) {
        for (const failureMetdata of series.failuresMetadata[index] ?? []) {
          relevantFailures.push({
            id: rowId,
            chemicalElementSymbol: key.chemicalElementSymbol,
            ...failureMetdata,
          });
          rowId++;
        }
      }
    }
  }

  return relevantFailures;
};

const useBuildColumns = (): GridColDef[] => {
  const { t } = useTenantTranslation();
  const theme = useTheme();

  const formatNumber = useFormatNumber();
  const formatWeightPercent = formatNumber(3, true);
  const formatExcessFraction = (value?: number) =>
    !value ? "" : `${formatNumber(1, true)(value * 100)} %`;

  return [
    { field: "heat_id", headerName: t("heat"), flex: 1 },
    { field: "sample_timestamp", headerName: t("sampleTimestamp"), flex: 2 },
    { field: "steel_grade_name", headerName: t("steelGrade"), flex: 2 },
    { field: "product_group_name", headerName: t("productGroup"), flex: 2 },
    {
      field: "chemicalElementSymbol",
      headerName: t("element"),
      flex: 1,
      renderCell: (
        params: GridRenderCellParams<
          FailureRow,
          ChemicalElementSymbol | NonElementQuantity
        >
      ) =>
        !params.value ? (
          ""
        ) : (
          <ColouredCell
            content={params.value}
            alpha={0.5}
            color={
              chemicalElementColours.get(params.value) ??
              theme.palette.common.white
            }
            justifyContent="center"
          />
        ),
      cellClassName: "coloured-cell",
    },
    {
      field: "weight_percent",
      headerName: "%",
      flex: 1,
      align: "right",
      valueFormatter: formatWeightPercent,
    },
    {
      field: "weight_percent_min",
      headerName: t("minPercent"),
      flex: 1,
      align: "right",
      valueFormatter: formatWeightPercent,
    },
    {
      field: "weight_percent_max",
      headerName: t("maxPercent"),
      flex: 1,
      align: "right",
      valueFormatter: formatWeightPercent,
    },
    {
      field: "violation_fraction",
      headerName: t("violation"),
      flex: 1,
      renderCell: (params: GridRenderCellParams<FailureRow, number>) =>
        !params.value ? (
          ""
        ) : (
          <ColourGradientCell
            content={formatExcessFraction(params.value)}
            alphaVariableValue={params.value}
            alphaVariableLowerClippingValue={0}
            alphaVariableUpperClippingValue={0.3}
            color={theme.palette.data.red}
            justifyContent="flex-end"
          />
        ),
      align: "right",
      cellClassName: "coloured-cell",
    },
  ];
};
